import api from "../../utils/api";

export interface MonthlyTrend {
  year: number;
  monthNumber: number;
  month: string;
  calculations: number;
}

export interface CalculatorUsage {
  calculator_type: string;
  label?: string;
  count: number;
}

export interface DailyTrend {
  date: string;
  calculations: number;
}

export interface NamedCount {
  key: string;
  label: string;
  count: number;
}

export interface WeekdayTrend {
  weekday: string;
  weekdayNumber: number;
  calculations: number;
}

export interface HourlyTrend {
  hour: number;
  label: string;
  calculations: number;
}

export interface DailyByCalculator {
  date: string;
  calculator_type: string;
  label: string;
  count: number;
}

export interface HerPhasesEngagement {
  available: boolean;
  source?: string;
  error?: string;
  herPhasesOpened?: number;
  calculatorOpens?: number;
  calculateClicks?: number;
  resultsViewed?: number;
  summary?: {
    total_events: number;
    unique_visitors: number;
    unique_anonymous: number;
    unique_unknown: number;
    unique_registered: number;
    sessions: number;
    avg_dwell_sec: number;
  };
  by_channel?: Array<{
    channel: string;
    events: number;
    unique_visitors: number;
    unique_anonymous: number;
  }>;
  trend?: Array<{
    date: string;
    events: number;
    unique_visitors: number;
    unique_anonymous: number;
  }>;
  by_page?: Array<{
    page: string;
    label: string;
    events: number;
    unique_visitors: number;
    avg_dwell_sec: number;
  }>;
  by_event?: Array<{
    event_type: string;
    label: string;
    events: number;
    unique_visitors: number;
  }>;
  recent_visitors?: Array<{
    visitor_id: string;
    visitor_type: string;
    channel: string;
    events: number;
    sessions: number;
    last_seen: string;
    last_page_label: string;
    last_event_label: string;
  }>;
}

export interface HerPhasesOutreach {
  available: boolean;
  source?: string;
  total: number;
  byStatus: Array<{ status: string; count: number }>;
  recent: Array<{
    userId: string;
    eventType: string;
    status: string;
    templateName?: string | null;
    sendAt?: string;
    sentAt?: string | null;
    createdAt: string;
    lastError?: string | null;
  }>;
  error?: string;
}

export interface HerPhasesOverview {
  totalUsers: number;
  totalCalculations: number;
  averageCalculationsPerUser: number;
  filteredCalculations: number;
  guestCalculations: number;
  registeredUsers: number;
  returningUsers: number;
  firstTimeUsers: number;
  repeatRate: number;
  averageCycleLength: number;
  averageCycleLengthHealthy: number;
  averagePeriodDuration: number;
  mostUsedCalculator: string;
  mostUsedCalculatorLabel: string;
  calculatorUsage: CalculatorUsage[];
  monthlyTrend: MonthlyTrend[];
  dailyTrend: DailyTrend[];
  weekdayTrend: WeekdayTrend[];
  hourlyTrend: HourlyTrend[];
  cycleLengthBuckets: NamedCount[];
  periodDurationBuckets: NamedCount[];
  dailyByCalculator: DailyByCalculator[];
  engagement?: HerPhasesEngagement;
  outreach?: HerPhasesOutreach;
  conversion?: {
    visitors: number;
    herPhasesOpened: number;
    calculatorOpens?: number;
    calculateClicks?: number;
    resultsViewed?: number;
    calculations: number;
    openToCalculateRate: number;
    hubToCalculatorRate?: number;
    calculatorToResultRate?: number;
  };
}

interface HerPhasesOverviewResponse {
  success: boolean;
  data: HerPhasesOverview;
}

export interface HerPhasesUser {
  user_id: string;
  user_name: string;
  phone_number: string;
  email_id: string;
  totalCalculations: number;
  lastActivity: string;
  firstActivity?: string;
  periodCount?: number;
  ovulationCount?: number;
  pregnancyTestCount?: number;
  menstrualCycleCount?: number;
}

export interface UserCalculationHistory {
  calculator_type: string;
  cycle_length: number;
  period_duration: number;
  last_period_date: string;
  ovulation_date: string;
  fertile_window_start: string;
  fertile_window_end: string;
  pregnancy_test_date: string;
  follicular_phase_start?: string;
  follicular_phase_end?: string;
  luteal_phase_start?: string;
  luteal_phase_end?: string;
  cycle_start_date?: string;
  next_period_date?: string;
  createdAt: string;
}

function toDateOnly(value: Date): string {
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const d = String(value.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export const herPhasesAnalyticsService = {
  getOverview: async (from: Date, to: Date): Promise<HerPhasesOverviewResponse> => {
    const response = await api.get<HerPhasesOverviewResponse>("/herPhases/analytics", {
      params: { from: toDateOnly(from), to: toDateOnly(to) },
    });
    return response.data;
  },
  getUsers: async (search = "", page = 1, limit = 10) => {
    const response = await api.get("/herPhases/analytics/users", {
      params: { search, page, limit },
    });
    return response.data;
  },
  getUserCalculationHistory: async (userId: string) => {
    const response = await api.get("/herPhases/analytics/user-history", {
      params: { userId },
    });
    return response.data;
  },
};
